import { ChangeDetectorRef, Component, computed, effect, EffectRef, ElementRef, inject, Signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { ButtonType } from '../../../../../models/enums/components/Button/ButtonType.enum';
import { Search } from '../../../../../models/interfaces/components/Search.interface';
import { Configuration } from '../../../../../models/interfaces/configuration/index.interface';
import { ButtonColors } from '../../../../../models/enums/components/Button/ButtonColor.enum';

import { SearchAdvancedComponent } from './search-advanced/search-advanced.component';
import { SearchSimpleComponent } from './search-simple/search-simple.component';
import { AppViews } from '../../../../../models/enums/dvm/DvmViews.enum';
import { SearchTypes } from '../../../../../models/enums/components/Search/SearchTypes.enum';
import { SearchService } from '../../../../../services/components/search.service';
import anime from 'animejs';

@Component({
    selector: 'app-search',
    standalone: true,
    imports: [
    ButtonComponent,
    SearchSimpleComponent,
    SearchAdvancedComponent
],
    templateUrl: './search.component.html',
    styleUrl: './search.component.css'
})
export class SearchComponent implements Search {

  // VIEW CHILDS
  @ViewChild('searchContainer')
  private searchContainer!:       ElementRef<HTMLDivElement>;

  @ViewChild('searchWrapper')
  private searchWrapper!:         ElementRef<HTMLDivElement>;


  // SERVICES
  private readonly SearchService: SearchService                           = inject(SearchService);
  private readonly cdr:           ChangeDetectorRef                       = inject(ChangeDetectorRef);
  private readonly view:          Signal<AppViews|null>                   = this.SearchService.view;

  
  // CONFIGURATION
  private readonly searchConfig:  Signal<Configuration['Ui']['Search']>   = this.SearchService.searchConfig;
  public  readonly hasSearch:     Search['hasSearch']                     = computed((): boolean => this.searchConfig().hasSearch);
  public  readonly type:          Search['type']                          = computed((): SearchTypes  => this.searchConfig().type ?? SearchTypes.Simple);
  public  readonly order:         Search['order']                         = computed((): 'JSON'|'alphabetical' => this.searchConfig().order ?? 'JSON');
  public  readonly fileData:      Search['fileData']                      = computed((): Record<string, Record<string, string[]>> => this.searchConfig().fileData ?? {});


  // GLOBAL STATE
  public  readonly searchActive:  Signal<boolean|null>                    = this.SearchService.searchActive;
  protected readonly isAnimating: WritableSignal<boolean>                 = this.SearchService.isAnimating;
  private readonly animation:     number                                  = this.SearchService.duration;

  
  // COMPONENT STATE
  public  showSearch:             boolean                                 = false;
  public  hideSearchByView:       boolean                                 = true;
  
 
  // BUTTONS
  public readonly buttonType:     ButtonType                              = ButtonType.Pill;
  public readonly buttonColors:   typeof ButtonColors                     = ButtonColors;

  // SEARCHERS
  public readonly searchTypes:    typeof SearchTypes                      = SearchTypes;
  

  // ACTIONS

  /**
   * Método que activa o desactiva el Search.
   * @returns {void}
   */
  public toggleSearchButton(): void {
    this.SearchService.toggleSearch()
  }

  private searchVisbility(show: boolean): void {

    if(this.hideSearchByView && show) {
      this.hideSearchByView = false;
      this.cdr.markForCheck();
    }

    const wrapper: HTMLDivElement | undefined = this.searchWrapper?.nativeElement;

    if(!wrapper) return;

    anime({
      targets: wrapper,
      opacity: show ? 1 : 0,
      easing: show ? 'easeInOutSine' : 'easeInOutSine',
      duration: 300,
      complete: (): void => {
        this.hideSearchByView = !show;
        this.cdr.markForCheck();
      }
    })

  }

  // EFFECTS
  /**
   * Efecto que reacciona al cambio de la signal searchActive.
   * Si el valor es null, no hace nada.
   * Si no, lanza la animación en función del valor de searchActive. (si es true se muestra, si es false se oculta)
   */
  public animateSearch: EffectRef = effect((): void => {

    const isActive:   boolean | null  = this.searchActive()!,
          container:  HTMLElement     = this.searchContainer?.nativeElement;

    if(isActive === null || !container) return;

    if(isActive) {
      this.showSearch = true;
      this.cdr.markForCheck();
    }

    // Al comenzar y finalizar la animación, el estado de isAnimating se actualiza.
    // Si active es false, al finalizar se establece showSearch a false para aplicar 'hidden' al contenedor. (display: none)
    anime({
      targets: container,
      duration: this.animation,
      easing: 'easeInOutSine',
      opacity: isActive ? 1 : 0,
      begin:    (): void => this.isAnimating.set(true),
      complete: (): void => {
        if(!isActive) this.showSearch = false;
        this.isAnimating.set(false);
        this.cdr.markForCheck();
      }
    })

  });


  public toggleSearchVisibility: EffectRef = effect((): void => {

    const view: AppViews | null = this.view();

    if(view === null) return;
    
    if(untracked(this.searchActive)) this.SearchService.toggleSearch();

    switch(view) {

      case AppViews.Topview:
      case AppViews.Main:
        
        this.searchVisbility(true);

      break;

      case AppViews.Seatmap: 
      case AppViews.Pano:

        this.searchVisbility(false);
        
      break;

    }

  });

}
